import React, { useState, useEffect } from 'react';
import './ReportsListView.css';

/**
 * Компонент для отображения списка WB отчётов по неделям
 * с группировкой по годам
 */
export function WbReportsListView({ weeks, onViewWeek, onOpenProgressPanel, onDeleteWeek, onDeleteAll, onRefresh }) {
  const [expandedYears, setExpandedYears] = useState(new Set());
  const [groupedWeeks, setGroupedWeeks] = useState({});

  useEffect(() => {
    // Группируем недели по годам (берём год из начала периода)
    const grouped = {};
    weeks.forEach(week => {
      // Skip invalid weeks
      if (!week || typeof week !== 'string') return;

      const year = week.substring(0, 4);  // YYYY-MM-DD - YYYY-MM-DD
      if (!grouped[year]) {
        grouped[year] = [];
      }
      grouped[year].push(week);
    });

    // Сортируем года по убыванию
    const sorted = {};
    Object.keys(grouped).sort((a, b) => b - a).forEach(year => {
      sorted[year] = grouped[year].sort((a, b) => b.localeCompare(a));
    });

    setGroupedWeeks(sorted);

    // Автоматически раскрываем текущий год
    const currentYear = new Date().getFullYear().toString();
    if (sorted[currentYear]) {
      setExpandedYears(new Set([currentYear]));
    }
  }, [weeks]);

  const toggleYear = (year) => {
    const newExpanded = new Set(expandedYears);
    if (newExpanded.has(year)) {
      newExpanded.delete(year);
    } else {
      newExpanded.add(year);
    }
    setExpandedYears(newExpanded);
  };

  const expandAll = () => {
    setExpandedYears(new Set(Object.keys(groupedWeeks)));
  };

  const collapseAll = () => {
    setExpandedYears(new Set());
  };

  const years = Object.keys(groupedWeeks);

  if (years.length === 0) {
    return (
      <div className="reports-list-empty">
        <div className="empty-icon">📦</div>
        <h3>Нет загруженных отчётов</h3>
        <p>Начните загрузку отчётов, нажав кнопку ниже</p>
        <p style={{ fontSize: '13px', color: '#999', marginTop: '10px' }}>
          💡 Если загрузка уже идёт - дождитесь завершения и нажмите "🔄 Обновить"
        </p>
        <button
          className="btn-primary-large"
          onClick={onOpenProgressPanel}
        >
          Загрузить отчёты
        </button>
      </div>
    );
  }

  return (
    <div className="reports-list-view">
      {/* Заголовок и кнопка загрузки */}
      <div className="reports-list-header">
        <div className="reports-list-title">
          <h3>Загруженные отчёты</h3>
          <span className="reports-count">{weeks.length} недель</span>
        </div>
        <div className="reports-list-actions">
          <button className="btn-link" onClick={expandAll}>
            Развернуть все
          </button>
          <button className="btn-link" onClick={collapseAll}>
            Свернуть все
          </button>
          {onRefresh && (
            <button className="btn-link" onClick={onRefresh}>
              🔄 Обновить
            </button>
          )}
          <button
            className="btn-danger"
            onClick={() => {
              if (window.confirm('Удалить все WB отчёты? Это действие нельзя отменить.')) {
                onDeleteAll();
              }
            }}
            style={{ marginRight: '8px' }}
          >
            Удалить всё
          </button>
          <button
            className="btn-primary"
            onClick={onOpenProgressPanel}
          >
            Загрузить отчёты
          </button>
        </div>
      </div>

      {/* Список по годам */}
      <div className="reports-years-list">
        {years.map(year => {
          const isExpanded = expandedYears.has(year);
          const yearWeeks = groupedWeeks[year];

          return (
            <div key={year} className="year-group">
              <div
                className="year-header"
                onClick={() => toggleYear(year)}
              >
                <span className="year-toggle">
                  {isExpanded ? '▼' : '▶'}
                </span>
                <span className="year-label">{year}</span>
                <span className="year-badge">{yearWeeks.length} нед.</span>
              </div>

              {isExpanded && (
                <div className="year-months">
                  <table className="months-table">
                    <thead>
                      <tr>
                        <th>Период</th>
                        <th>Начало</th>
                        <th>Окончание</th>
                        <th>Статус</th>
                        <th>Действия</th>
                      </tr>
                    </thead>
                    <tbody>
                      {yearWeeks.map(week => {
                        const [start, end] = week.split(' - ');
                        return (
                          <tr key={week} className="month-row">
                            <td className="month-period">{week}</td>
                            <td className="month-name">{formatWeekDate(start)}</td>
                            <td className="month-name">{formatWeekDate(end)}</td>
                            <td>
                              <span className="status-badge status-loaded">
                                Загружен
                              </span>
                            </td>
                            <td>
                              <button
                                className="btn-view"
                                onClick={() => onViewWeek(week)}
                                style={{ marginRight: '8px' }}
                              >
                                Просмотр
                              </button>
                              <button
                                className="btn-danger-small"
                                onClick={() => {
                                  if (window.confirm(`Удалить отчёт за неделю ${week}?`)) {
                                    onDeleteWeek(week);
                                  }
                                }}
                              >
                                Удалить
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function formatWeekDate(dateStr) {
  // YYYY-MM-DD -> DD.MM.YYYY
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  return `${parts[2]}.${parts[1]}.${parts[0]}`;
}
